import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { usd } from "../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/gtax/admin/returns`;
const money = (v) => (v == null ? "—" : (v < 0 ? "-$" : "$") + Math.abs(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const err = (e) => { const d = e.response?.data?.detail; return typeof d === "string" ? d : "Something went wrong."; };
const TH = ({ children, r }) => <th className={`py-3 px-3 text-xs uppercase tracking-widest text-[color:var(--mute)] ${r ? "text-right" : "text-left"}`}>{children}</th>;
const TD = ({ children, r, ...p }) => <td {...p} className={`py-3 px-3 ${r ? "text-right tabular-nums" : ""}`}>{children}</td>;

const download = async (url, h, name) => {
  const res = await axios.get(url, { ...h, responseType: "blob" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(res.data);
  a.download = name;
  a.click();
};

const Totals = ({ t }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-px bg-[color:var(--line)]" data-testid="gtax-returns-totals">
    {[["Pool P&L", t.pnl], ["Trader 15%", t.mang], ["Referrals", t.refs], ["GTA X share", t.gtax], ["VIP III bonus", t.vip3_bonus], ["Recovered losses", t.recovered], ["Clients net share", t.clients]].map(([k, v]) => (
      <div key={k} className="bg-[color:var(--bg)] p-5" data-testid={`gtax-returns-total-${k.split(" ")[0].toLowerCase()}`}>
        <div className="text-xs uppercase tracking-widest text-[color:var(--mute)]">{k}</div>
        <div className="gx-serif text-2xl mt-1">{money(v)}</div>
      </div>
    ))}
  </div>
);

const PnlPanel = ({ h, reload, rows }) => {
  const [d, setD] = useState("");
  const [v, setV] = useState("");
  const upload = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try { const { data } = await axios.post(`${API}/upload`, fd, h); toast.success(`Imported ${data.imported} trading days`); reload(); } catch (x) { toast.error(err(x)); }
    e.target.value = "";
  };
  const add = async () => {
    try { await axios.put(`${API}/pnl`, { date: d, pnl: Number(v) }, h); setV(""); reload(); } catch (x) { toast.error(err(x)); }
  };
  const del = async (day) => {
    if (!window.confirm(`Remove trading day ${day}?`)) return;
    try { await axios.delete(`${API}/pnl/${day}`, h); reload(); } catch (x) { toast.error(err(x)); }
  };
  return (
    <div className="space-y-6" data-testid="gtax-returns-pnl">
      <div className="flex flex-wrap gap-6 items-end">
        <label className="gx-btn cursor-pointer" data-testid="gtax-returns-upload-label">Upload MT5 statement
          <input type="file" accept=".html,.htm,.xlsx,.xls,.csv" onChange={upload} className="hidden" data-testid="gtax-returns-upload" /></label>
        <div><div className="text-xs uppercase tracking-widest text-[color:var(--mute)]">Date</div><input type="date" value={d} onChange={(e) => setD(e.target.value)} className="py-2" data-testid="gtax-returns-pnl-date" /></div>
        <div><div className="text-xs uppercase tracking-widest text-[color:var(--mute)]">Realised P&L (USD)</div><input type="number" value={v} onChange={(e) => setV(e.target.value)} className="py-2" data-testid="gtax-returns-pnl-value" /></div>
        <button disabled={!d || v === ""} onClick={add} className="gx-ghost disabled:opacity-40" data-testid="gtax-returns-pnl-add">Add / overwrite day</button>
      </div>
      <p className="text-xs text-[color:var(--mute)]">MT5: History tab → right-click → Report → HTML or Open XML. Closed-trade profit + swap + commission is grouped per day. Uploading again overwrites the same days.</p>
      <div className="max-h-80 overflow-y-auto"><table className="w-full text-sm"><thead><tr className="border-b border-[color:var(--line)]"><TH>Date</TH><TH r>P&L</TH><TH>Source</TH><TH /></tr></thead>
        <tbody>{rows.map((p) => (
          <tr key={p.date} className="border-b border-[color:var(--line)]" data-testid={`gtax-pnl-row-${p.date}`}><TD>{p.date}</TD><TD r>{money(p.pnl)}</TD><TD>{p.source}</TD>
            <TD><button onClick={() => del(p.date)} className="text-xs underline" data-testid={`gtax-pnl-del-${p.date}`}>Remove</button></TD></tr>))}</tbody></table></div>
    </div>
  );
};

const LedgerForm = ({ h, clients, reload }) => {
  const [f, setF] = useState({ reference: "", type: "deposit", amount: "", date: "", note: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const add = async () => {
    try { await axios.post(`${API}/ledger`, { ...f, amount: Number(f.amount) }, h); toast.success("Ledger entry added"); setF({ ...f, amount: "", note: "" }); reload(); } catch (x) { toast.error(err(x)); }
  };
  return (
    <div className="flex flex-wrap gap-4 items-end" data-testid="gtax-ledger-form">
      <select value={f.reference} onChange={set("reference")} className="p-2" data-testid="gtax-ledger-member"><option value="">Member…</option>
        {clients.map((c) => <option key={c.reference} value={c.reference}>{`${c.full_name} · ${c.reference}`}</option>)}</select>
      <select value={f.type} onChange={set("type")} className="p-2" data-testid="gtax-ledger-type"><option value="deposit">Deposit received</option><option value="withdrawal">Withdrawal paid</option></select>
      <input type="number" placeholder="Amount" value={f.amount} onChange={set("amount")} className="py-2 w-32" data-testid="gtax-ledger-amount" />
      <input type="date" value={f.date} onChange={set("date")} className="py-2" data-testid="gtax-ledger-date" />
      <input placeholder="Note / TXID" value={f.note} onChange={set("note")} className="py-2 w-48" data-testid="gtax-ledger-note" />
      <button disabled={!f.reference || !f.amount || !f.date} onClick={add} className="gx-ghost disabled:opacity-40" data-testid="gtax-ledger-add">Add</button>
    </div>
  );
};

const LedgerList = ({ h, rows, names, reload }) => {
  const del = async (e) => {
    if (!window.confirm(`Delete ${e.type} of ${money(e.amount)} for ${names[e.reference] || e.reference} on ${e.date}?`)) return;
    try { await axios.delete(`${API}/ledger/${e.id}`, h); reload(); } catch (x) { toast.error(err(x)); }
  };
  return (
    <div className="max-h-72 overflow-y-auto mt-6" data-testid="gtax-ledger-list">
      <table className="w-full text-sm"><thead><tr className="border-b border-[color:var(--line)]"><TH>Date</TH><TH>Member</TH><TH>Type</TH><TH r>Amount</TH><TH>Note</TH><TH /></tr></thead>
        <tbody>{rows.map((e) => (
          <tr key={e.id} className="border-b border-[color:var(--line)]" data-testid={`gtax-ledger-row-${e.id}`}>
            <TD>{e.date}</TD><TD>{names[e.reference] || e.reference}</TD><TD>{e.type}</TD><TD r>{money(e.amount)}</TD><TD>{e.note}</TD>
            <TD><button onClick={() => del(e)} className="text-xs underline" data-testid={`gtax-ledger-del-${e.id}`}>Delete</button></TD>
          </tr>))}</tbody></table>
    </div>
  );
};

const ClientRow = ({ c, h, reload, month, clients }) => {
  const save = async (patch) => { try { await axios.patch(`${API}/clients/${c.reference}`, patch, h); reload(); } catch (x) { toast.error(err(x)); } };
  return (
    <tr className="border-b border-[color:var(--line)] align-top" data-testid={`gtax-returns-client-${c.reference}`}>
      <TD>{c.full_name}<div className="text-xs font-mono text-[color:var(--mute)]">{c.reference}</div></TD>
      <TD><select value={c.tier} onChange={(e) => save({ tier: e.target.value })} className="p-1" data-testid={`gtax-returns-tier-${c.reference}`}>
        {["standard", "vip1", "vip2", "vip3"].map((t) => <option key={t} value={t}>{t.toUpperCase()}</option>)}</select></TD>
      <TD><select value={c.referred_by || ""} onChange={(e) => save({ referred_by: e.target.value })} className="p-1 max-w-[150px]" data-testid={`gtax-returns-ref-${c.reference}`}>
        <option value="">No referrer</option>{clients.filter((x) => x.reference !== c.reference).map((x) => <option key={x.reference} value={x.reference}>{x.full_name}</option>)}</select></TD>
      <TD r>{money(c.deposits || 0)}</TD><TD r>{money(c.withdrawals || 0)}</TD><TD r>{money(c.period_result)}</TD><TD r>{money(c.bonus || 0)}</TD>
      <TD r>{money(c.loss_to_recover)}</TD><TD r><b data-testid={`gtax-returns-balance-${c.reference}`}>{money(c.balance)}</b></TD>
      <TD><button onClick={() => download(`${API}/statement/${c.reference}?month=${month}`, h, `GTAX_${c.reference}_${month}.pdf`).catch(() => toast.error("No statement"))}
        className="text-xs underline whitespace-nowrap" data-testid={`gtax-returns-statement-${c.reference}`}>{month} PDF</button></TD>
    </tr>
  );
};

export const AdminReturns = ({ token }) => {
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [data, setData] = useState(null);
  const [pnl, setPnl] = useState([]);
  const [ledger, setLedger] = useState([]);
  const month = (end || new Date().toISOString()).slice(0, 7);
  const load = () => Promise.all([axios.get(`${API}/summary`, { ...h, params: { start: start || undefined, end: end || undefined } }), axios.get(`${API}/pnl`, h), axios.get(`${API}/ledger`, h)])
    .then(([s, p, l]) => { setData(s.data); setPnl(p.data); setLedger(l.data); })
    .catch((x) => toast.error(err(x)));
  useEffect(() => { load(); }, [start, end]); // eslint-disable-line react-hooks/exhaustive-deps
  const setMode = async (loss_mode) => { await axios.put(`${API}/settings`, { loss_mode }, h); load(); };
  if (!data) return <p>Loading…</p>;
  const clients = data.clients;
  return (
    <div className="space-y-14" data-testid="gtax-admin-returns">
      <div className="flex flex-wrap gap-6 items-end justify-between">
        <div className="flex gap-6 items-end">
          <div><div className="text-xs uppercase tracking-widest text-[color:var(--mute)]">From</div><input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="py-2" data-testid="gtax-returns-start" /></div>
          <div><div className="text-xs uppercase tracking-widest text-[color:var(--mute)]">To</div><input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="py-2" data-testid="gtax-returns-end" /></div>
        </div>
        <div className="flex gap-2 items-center text-sm" data-testid="gtax-returns-lossmode">
          <span className="text-[color:var(--mute)]">Losing days:</span>
          {[["recover_first", "Recover losses before profit shares"], ["pro_rata", "Losses just reduce balances"]].map(([id, l]) => (
            <button key={id} onClick={() => setMode(id)} className={`gx-ghost !px-3 !py-1 ${data.settings.loss_mode === id ? "!border-[color:var(--gold)] !text-[color:var(--gold2)]" : ""}`} data-testid={`gtax-returns-lossmode-${id}`}>{l}</button>
          ))}
        </div>
      </div>
      <Totals t={data.totals} />
      <section>
        <div className="flex justify-between items-end"><h3 className="gx-serif text-3xl">Weekly payout sheet</h3>
          <button onClick={() => download(`${API}/weekly.csv?${new URLSearchParams({ ...(start && { start }), ...(end && { end }) })}`, h, "gtax_weekly_payouts.csv")} className="gx-ghost" data-testid="gtax-returns-csv">Download CSV</button></div>
        <table className="w-full text-sm mt-4"><thead><tr className="border-b border-[color:var(--line)]"><TH>Week</TH><TH r>Days</TH><TH r>Pool P&L</TH><TH r>Trader 15%</TH><TH r>Referrals</TH><TH r>GTA X</TH><TH r>Clients</TH></tr></thead>
          <tbody>{data.weeks.map((w) => (<tr key={w.week} className="border-b border-[color:var(--line)]" data-testid={`gtax-week-${w.week}`}><TD>{w.week}</TD><TD r>{w.days}</TD><TD r>{money(w.pnl)}</TD><TD r>{money(w.mang)}</TD><TD r>{money(w.refs)}</TD><TD r>{money(w.gtax)}</TD><TD r>{money(w.clients)}</TD></tr>))}</tbody></table>
      </section>
      <section>
        <h3 className="gx-serif text-3xl">Referral commissions payable</h3>
        <table className="w-full text-sm mt-4"><tbody>{data.referrers.length ? data.referrers.map((r) => (<tr key={r.referrer} className="border-b border-[color:var(--line)]" data-testid={`gtax-referrer-${r.referrer}`}><TD>{r.name} <span className="text-xs font-mono text-[color:var(--mute)]">{r.referrer}</span></TD><TD r>{money(r.amount)}</TD></tr>)) : <tr><TD>No referral commissions in this period.</TD></tr>}</tbody></table>
      </section>
      <section>
        <h3 className="gx-serif text-3xl">Clients</h3>
        <p className="text-sm text-[color:var(--mute)] mt-2">Approved members only. Money starts earning on the first trading day after its ledger date. Results are reinvested automatically.</p>
        <div className="mt-6"><LedgerForm h={h} clients={clients} reload={load} /></div>
        <LedgerList h={h} rows={ledger} names={Object.fromEntries(clients.map((c) => [c.reference, c.full_name]))} reload={load} />
        <div className="overflow-x-auto mt-6"><table className="w-full text-sm"><thead><tr className="border-b border-[color:var(--line)]"><TH>Member</TH><TH>Tier</TH><TH>Referred by</TH><TH r>Deposited</TH><TH r>Withdrawn</TH><TH r>Period result</TH><TH r>VIP III bonus</TH><TH r>Loss to recover</TH><TH r>Balance</TH><TH>Statement</TH></tr></thead>
          <tbody>{clients.map((c) => <ClientRow key={c.reference} c={c} h={h} reload={load} month={month} clients={clients} />)}</tbody></table></div>
      </section>
      <section>
        <h3 className="gx-serif text-3xl">Daily trading results</h3>
        <div className="mt-6"><PnlPanel h={h} reload={load} rows={pnl} /></div>
      </section>
      <p className="text-xs text-[color:var(--mute)]">Total client capital: {usd(clients.reduce((a, c) => a + c.balance, 0))}</p>
    </div>
  );
};
